import {merge} from 'lodash-es';
import type {MaybeRef, Ref} from 'vue';
import type {Paginator, PaginatorGenerator} from '@/utils/Paginator';
import {abortController, computerToHumanIndexing, humanToComputerIndexing} from '@/utils';
import useImmediateHydration from '~/composables/useImmediateHydration';

interface PaginationOptions {
	linear?: boolean
	pageSize?: MaybeRef<number>
	initialPage?: number
	initialOffset?: number
}

interface Page<T> {
	items: T[]
	page: number,
	offset: number,
}

interface PaginationState<T> {
	offset: number
	totalItems: number
	page: number,
	totalPages: number,
	hasMore: boolean,
	pages: Page<T>[],
	items: T[],
}

export interface Pagination<T> {
	abort: () => void
	next: (replace?: boolean) => void
	prev: () => void
	reset: () => void
	pending: Ref<boolean>
	pagination: Ref<PaginationState<T> | null | undefined>

	// Signals that the currently held items are no longer valid, because a request to replace them is pending
	obsolete: Ref<boolean>
}

export default async function usePagination<T>(
	key: string,
	paginatorFactory: () => Paginator<T>,
	options: PaginationOptions = {},
): Promise<Pagination<T>> {
	const opts = merge({
		linear: false,
		pageSize: 1,
		initialPage: 1,
	}, options);

	const resolvedPageSize = ref(opts.pageSize);
	const initialOffset = humanToComputerIndexing(opts.initialPage) * resolvedPageSize.value;
	const requestedOffset = ref(initialOffset);
	const fetchedPages = ref([]) as Ref<Page<T>[]>;
	const pageAnimationName = ref<'page-forward' | 'page-backward'>('page-forward');
	const obsolete = ref(true);

	let abort: (() => void) = () => {
	};
	let generator: PaginatorGenerator<T> | null = null;

	useImmediateHydration(
		key,
		() => fetchedPages.value,
		(value) => {
			fetchedPages.value = value;
		},
	);

	const {data: pagination, refresh, pending} = await useAsyncData(key, async (): Promise<PaginationState<T> | null> => {
		try {
			abort();

			const gen = generator ?? createGenerator();
			const {signal, abort: newAbort} = abortController();

			if (import.meta.client) {
				generator = gen;
				abort = newAbort;
			}

			const result = await gen({signal, offset: requestedOffset.value});

			if (!result) {
				return null;
			}

			const offset = result.value?.offset ?? 0;
			const totalItems = result.value?.total ?? 0;

			const page: Page<T> = {
				items: result.value?.items ?? [],
				page: computerToHumanIndexing(Math.floor(offset / resolvedPageSize.value)),
				offset: requestedOffset.value,
			};

			// only current page on SSR
			if (import.meta.server) {
				fetchedPages.value = [page];
			} else {
				const shouldAppend = opts.linear && !obsolete.value;
				fetchedPages.value = shouldAppend
					? [...fetchedPages.value, page]
					: [page];
			}

			return {
				offset,
				totalItems,
				page: page.page,
				totalPages: Math.ceil(totalItems / resolvedPageSize.value),
				hasMore: !result.done,
				pages: fetchedPages.value,
				items: fetchedPages.value.flatMap(({items}) => items),
			};
		} finally {
			obsolete.value = false;
			if (import.meta.server) {
				abort();
				generator = null;
			}
		}
	}, {watch: [requestedOffset]});

	watch(resolvedPageSize, reset);

	return {
		pagination,
		next,
		prev,
		abort,
		reset,
		pending,
		obsolete,
	};

	function next(replace = false) {
		if (!pagination.value?.hasMore) {
			return;
		}

		obsolete.value = replace;
		pageAnimationName.value = 'page-forward';
		requestedOffset.value = pagination.value.offset + resolvedPageSize.value;
	}

	function prev() {
		if (!pagination.value) {
			return;
		}

		// Non-looping pagination would go to negative pages
		if (opts.linear && pagination.value.offset < resolvedPageSize.value) {
			return;
		}

		obsolete.value = true;
		pageAnimationName.value = 'page-backward';
		requestedOffset.value = pagination.value.offset - resolvedPageSize.value;
	}

	function createGenerator(offset?: number, total?: number) {
		const paginator = paginatorFactory();
		const args = [resolvedPageSize.value, offset, total] as const;

		return opts.linear
			? paginator.linear(...args)
			: paginator.looping(...args);
	}

	async function reset() {
		// abort any pending request
		abort();
		generator = null;
		fetchedPages.value = [];
		obsolete.value = true;

		if (requestedOffset.value === 0) {
			try {
				// Manually refresh the data, because the offset is the same
				await refresh({dedupe: 'cancel'});
			} finally {
				obsolete.value = false;
			}
		} else {
			// Reset the offset to 0, which will trigger a refresh automatically via `watch`
			requestedOffset.value = 0;
		}
	}
}
